/**
 * JIT 코드 컴파일 훅.
 * LLM이 생성한 React/Remotion 코드 문자열을 브라우저에서 컴파일하여 React 컴포넌트로 변환.
 *
 * 참고: https://www.remotion.dev/docs/ai/dynamic-compilation
 *
 * 원본 위치: features/remotion-editor/editor/items/code/use-code-compilation.ts
 * shared 승격 사유: features/gts-step·remotion-editor·output 등 다수 슬라이스에서
 *   동일 훅이 필요 → FSD cross-slice import 회피.
 *
 * v2.1 통합 (compiler integration):
 *   본 훅은 자체 컴파일러를 들고 있지 않고, **AI sandbox runtime의 canonical 컴파일러**
 *   인 `@/features/vibemotion/remotion/compiler` 의 `compileCode` 에 위임한다.
 *
 *   이유: 렌더 경로 (Remotion CLI → DynamicChunksComp → vibemotion/remotion/compiler)
 *   와 미리보기 경로 (Player → MSceneChunksPreview → useCodeCompilation) 가 동일한
 *   sandbox 글로벌 (FONTS, Recipes, RemotionShapes, TransitionSeries, fade/slide/...,
 *   Three, Lottie) 과 동일한 sanitization (smart-quote, FONTS re-decl strip, decorative
 *   comment 제거, spring fix) 을 사용해야 **preview ≡ render** 가 보장됨.
 *
 *   FSD 관점: shared → features 임포트가 발생한다. 이 의존은 *AI sandbox runtime contract*
 *   가 vibemotion (render) 과 gts-step·output·remotion-editor (preview) 양쪽에서 공유되기
 *   때문에 의도적이다. 향후 sandbox 자체를 shared/lib/remotion/sandbox 로 승격할 수 있음.
 *
 *   Babel 지연 로드: vibemotion compiler 모듈은 `@babel/standalone` (~4MB) 을 top-level
 *   import 한다. shared 훅이 이를 정적으로 import 하면 hook 을 사용하는 모든 페이지가
 *   babel + 4 google fonts + Three/Lottie 를 즉시 받는다 → SSR 번들 폭발. 따라서 동적
 *   import 로 첫 컴파일 직전에만 로드한다.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import {
  cancelRender,
  continueRender,
  delayRender,
  getRemotionEnvironment,
} from 'remotion';

interface CompilationResult {
  Component: React.ComponentType | null;
  error: string | null;
}

// vibemotion compiler module 의 동적 import 캐시.
// 4MB 의 babel-standalone + google-fonts/Jua/NotoSansKR/Inter/JetBrainsMono 등의
// side-effect 가 들어있어 첫 컴파일 직전까지 로드를 미룬다.
let compilerPromise: Promise<
  typeof import('@/features/vibemotion/remotion/compiler')
> | null = null;
let compilerModule:
  | typeof import('@/features/vibemotion/remotion/compiler')
  | null = null;

async function loadCompiler() {
  if (compilerModule) {
    return compilerModule;
  }
  if (!compilerPromise) {
    console.log('[Compiler] dynamic import 시작 (babel + fonts + recipes)');
    compilerPromise = import('@/features/vibemotion/remotion/compiler');
  }
  compilerModule = await compilerPromise;
  console.log('[Compiler] dynamic import 완료');
  return compilerModule;
}

/**
 * Remotion 코드 문자열을 React 컴포넌트로 JIT 컴파일하는 훅.
 *
 * 내부적으로 vibemotion sandbox compileCode 에 위임 → render 경로와 byte-identical
 * 한 글로벌 / sanitization 을 보장한다.
 *
 * @param code - React/Remotion 소스 코드 문자열
 * @returns { Component, error } - 컴파일된 컴포넌트 또는 에러
 *
 * @example
 * ```tsx
 * const { Component, error } = useCodeCompilation(item.code);
 * if (error) return <ErrorOverlay message={error} />;
 * if (!Component) return null;
 * return <Component />;
 * ```
 */
export function useCodeCompilation(code: string): CompilationResult {
  const [result, setResult] = useState<CompilationResult>({
    Component: null,
    error: null,
  });

  useEffect(() => {
    if (!code?.trim()) {
      setResult({ Component: null, error: null });
      return;
    }

    // Lambda 렌더 중에만 delayRender 핸들을 걸어 컴파일 완료 전 프레임 캡처를 막는다.
    // Player/일반 React 컨텍스트(예: OutputCanvasRemotionPreview)에선 no-op 로 동작해
    // 기존 미리보기 동작을 그대로 유지한다.
    const isRendering = getRemotionEnvironment().isRendering;
    const handle = isRendering
      ? delayRender('Compiling code item (useCodeCompilation)')
      : null;

    let cancelled = false;
    let handleContinued = false;
    const safeContinueRender = () => {
      if (handle === null || handleContinued) return;
      handleContinued = true;
      continueRender(handle);
    };

    const compile = async () => {
      try {
        const mod = await loadCompiler();
        if (cancelled) {
          safeContinueRender();
          return;
        }
        await mod.waitForSandboxFonts();
        if (cancelled) {
          safeContinueRender();
          return;
        }
        const compiled = mod.compileCode(code);
        if (cancelled) {
          safeContinueRender();
          return;
        }
        setResult(compiled);
        // Preview keeps the compiler's error UI. A render must never publish
        // that UI (or a missing component) as a successfully compiled scene.
        if (handle !== null && (compiled.error || !compiled.Component)) {
          handleContinued = true;
          cancelRender(new Error(compiled.error || '코드 장면을 만들지 못했습니다.'));
          return;
        }
        safeContinueRender();
      } catch (e) {
        if (cancelled) {
          safeContinueRender();
          return;
        }
        console.error('[CodeCompilation] Compiler load/run failed:', e);
        setResult({
          Component: null,
          error:
            e instanceof Error
              ? e.message
              : '컴파일러 로드 실패 (unknown error)',
        });
        // 렌더 경로: 에러 프레임을 대기하지 않도록 cancelRender 로 렌더 중단.
        // 프리뷰 경로(handle === null): no-op.
        if (handle !== null && !handleContinued) {
          handleContinued = true;
          cancelRender(e instanceof Error ? e : new Error(String(e)));
        }
      }
    };

    void compile();

    return () => {
      cancelled = true;
      safeContinueRender();
    };
  }, [code]);

  return result;
}

/**
 * Babel + sandbox compiler 를 사전 로드합니다 (코드 생성 모드 진입 시 호출).
 *
 * 첫 미리보기 시점에 4MB 다운로드를 피하려면 사용자가 코드 생성을 시작하는 순간
 * (예: Step2 진입) 이 함수를 fire-and-forget 으로 호출해두면 된다.
 */
export async function preloadBabel(): Promise<void> {
  await loadCompiler();
}
